import { useTranslation } from "react-i18next";
import { CircleSlash, MessageCircle, Sparkles } from "lucide-react";
import { RoiCalculator } from "@/components/home/RoiCalculator";
import { PlanCard } from "@/components/pricing/PlanCard";
import { TextLink } from "@/components/ui/Button";
import { AuditButton, CtaReassurance, FreeAuditButton, WhatsAppButton } from "@/components/ui/Cta";
import { LitPanel } from "@/components/ui/LitPanel";
import { PageHero } from "@/components/ui/PageHero";
import { SectionHeader } from "@/components/ui/SectionHeader";
import { FaqSection } from "@/components/sections/FaqSection";
import { FinalCta } from "@/components/sections/FinalCta";
import { useContent } from "@/data/content";
import { formatEuro, formatRate, usePricing } from "@/data/pricing";
import { usePageSeo } from "@/hooks/usePageSeo";
import type { AppLocale } from "@/i18n/constants";
import { useAppTranslation } from "@/i18n/useAppTranslation";

export function PricingPage() {
  usePageSeo("/precios");
  const pricing = usePricing();
  const { plans } = useContent().home;
  const { t } = useAppTranslation("nav");
  const { i18n } = useTranslation();
  const locale = (i18n.language === "en" ? "en" : "es") as AppLocale;
  const en = locale === "en";

  return (
    <main id="main-content" tabIndex={-1} className="outline-none">
      <PageHero
        crumb={t("pricing")}
        title={pricing.page.title}
        lead={pricing.page.lead}
        actions={<FreeAuditButton location="pricing_hero" variant="primary" />}
      />

      <section aria-labelledby="plans-heading" className="bg-white">
        <div className="mx-auto max-w-7xl px-4 py-20 sm:px-6 md:py-28 lg:px-8">
          <SectionHeader id="plans-heading" title={pricing.page.plansTitle} lead={plans.subtitle} />
          <div className="mt-16 grid gap-6 sm:grid-cols-2 lg:grid-cols-4 lg:gap-5">
            {pricing.packs.map((pack, i) => (
              <PlanCard
                key={pack.id}
                pack={pack}
                pricing={pricing}
                postsLabel={plans.postsLabel}
                foundationLabel={plans.foundationLabel}
                detailed
                delay={i * 80}
              />
            ))}
          </div>
          <p className="mt-8 text-sm text-ink-muted">
            {pricing.taxNote} {pricing.networkNote}
          </p>

          <RoiCalculator />

          <div className="mt-14 grid gap-6 lg:grid-cols-2">
            <div data-reveal="" className="flex flex-col rounded-[1.75rem] bg-teal-soft p-8 sm:p-10">
              <MessageCircle aria-hidden="true" className="size-7 text-teal-strong" />
              <h3 className="mt-5 font-display text-2xl font-semibold text-brand-primary">{pricing.firstStep.title}</h3>
              <p className="mt-3 leading-relaxed text-ink-soft">{pricing.firstStep.body}</p>
              <div className="mt-auto pt-8">
                <FreeAuditButton location="pricing_first_step" variant="primary" />
                <CtaReassurance className="mt-3" />
              </div>
            </div>
            <div data-reveal="" style={{ ["--reveal-delay" as string]: "90ms" }} className="flex flex-col rounded-[1.75rem] border border-brand-primary/10 bg-brand-bg p-8 sm:p-10">
              <Sparkles aria-hidden="true" className="size-7 text-teal-strong" />
              <h3 className="mt-5 font-display text-2xl font-semibold text-brand-primary">{pricing.custom.title}</h3>
              <p className="mt-3 leading-relaxed text-ink-soft">{pricing.custom.body}</p>
              <ul className="mt-5 space-y-2">
                {pricing.custom.factors.map((factor) => (
                  <li key={factor} className="node text-ink">
                    {factor}
                  </li>
                ))}
              </ul>
              <p className="mt-5 text-sm text-ink-muted">{pricing.custom.note}</p>
              <div className="mt-auto pt-8">
                <WhatsAppButton
                  location="pricing_custom"
                  message={en ? "Hi Jenifer, I'd like a custom plan." : "Hola Jenifer, me interesa un plan a medida."}
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      <section id="servicios" aria-labelledby="services-price-heading" className="bg-brand-bg">
        <div className="mx-auto max-w-7xl px-4 pt-20 sm:px-6 md:pt-28 lg:px-8">
          <SectionHeader id="services-price-heading" title={pricing.services.title} lead={pricing.services.intro} />
          <ul className="mt-12 grid gap-6 md:grid-cols-2">
            {pricing.services.items.map((item, i) => (
              <LitPanel as="li" key={item.id} className="flex flex-col p-8 sm:p-9">
                <div data-reveal="" style={{ ["--reveal-delay" as string]: `${i * 80}ms` }} className="flex flex-1 flex-col">
                  <div className="flex flex-wrap items-start justify-between gap-x-6 gap-y-3">
                    <h3 className="max-w-xs font-display text-2xl font-semibold leading-tight text-brand-primary">{item.title}</h3>
                    <p className="sm:text-right">
                      {item.from ? <span className="mr-1.5 text-sm text-ink-muted">{pricing.from}</span> : null}
                      <span className="font-display text-3xl font-semibold text-brand-primary">{formatEuro(item.price, locale)}</span>
                      <span className="block text-sm text-ink-muted">{item.cadence === "month" ? pricing.perMonth : pricing.oneOff}</span>
                    </p>
                  </div>
                  <p className="mt-4 leading-relaxed text-ink-soft">{item.desc}</p>
                  {item.extra ? <p className="node mt-4 text-sm font-semibold text-teal-strong">{item.extra}</p> : null}
                  <div className="mt-auto flex flex-wrap items-center gap-x-6 gap-y-3 pt-7">
                    {item.href ? <TextLink to={item.href}>{pricing.services.cta}</TextLink> : null}
                    {item.id === "asesoria" ? (
                      <AuditButton location={`service_${item.id}`} variant="outline" size="md" />
                    ) : (
                      <WhatsAppButton
                        location={`service_${item.id}`}
                        variant="outline"
                        size="md"
                        label={en ? "Ask on WhatsApp" : "Consultar por WhatsApp"}
                        message={en ? `Hi Jenifer, I'd like to know more about ${item.title}.` : `Hola Jenifer, quiero saber más sobre ${item.title}.`}
                      />
                    )}
                  </div>
                </div>
              </LitPanel>
            ))}
          </ul>
        </div>
      </section>

      <section id="sueltos" aria-labelledby="rates-heading" className="bg-brand-bg">
        <div className="mx-auto grid max-w-7xl gap-14 px-4 py-20 sm:px-6 md:py-28 lg:grid-cols-[0.8fr_1.2fr] lg:gap-20 lg:px-8">
          <div>
            <SectionHeader id="rates-heading" title={pricing.rateCard.title} lead={pricing.rateCard.intro} />
            <p className="mt-4 text-sm text-ink-muted">
              {pricing.rateCard.note} {pricing.taxNote}
            </p>
          </div>
          <table className="w-full border-collapse text-left">
            <caption className="sr-only">{pricing.rateCard.title}</caption>
            <thead className="sr-only">
              <tr>
                <th scope="col">{pricing.page.tableService}</th>
                <th scope="col">{pricing.page.tablePrice}</th>
              </tr>
            </thead>
            <tbody>
              {pricing.rateCard.items.map((item) => (
                <tr key={item.service} className="border-b border-brand-primary/10 transition-colors duration-300 hover:bg-teal-soft/60">
                  <th scope="row" className="py-5 pr-4 align-top font-normal">
                    <span className="flex items-baseline gap-3">
                      <span className="shrink-0 font-display text-xl font-semibold text-brand-primary">{item.service}</span>
                      <span aria-hidden="true" className="mb-1.5 hidden flex-1 border-b-2 border-dotted border-brand-primary/20 sm:block" />
                    </span>
                    <span className="mt-1 block text-base text-ink-soft">{item.detail}</span>
                  </th>
                  <td className="whitespace-nowrap py-5 text-right align-top font-display text-xl font-semibold text-brand-primary">
                    {formatRate(item, locale)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      <section aria-labelledby="addons-heading" className="bg-white">
        <div className="mx-auto max-w-7xl px-4 py-20 sm:px-6 md:py-28 lg:px-8">
          <SectionHeader id="addons-heading" title={pricing.addOns.title} lead={pricing.addOns.intro} />
          <ul className="mt-12 grid gap-6 md:grid-cols-3">
            {pricing.addOns.items.map((item, i) => (
              <LitPanel as="li" key={item.title} className="p-8">
                <div data-reveal="" style={{ ["--reveal-delay" as string]: `${i * 80}ms` }}>
                  <h3 className="font-display text-xl font-semibold text-brand-primary">{item.title}</h3>
                  <p className="mt-3 leading-relaxed text-ink-soft">{item.desc}</p>
                  <p className="mt-5 text-sm font-semibold text-teal-strong">{item.by}</p>
                </div>
              </LitPanel>
            ))}
          </ul>

          <div className="mt-16 grid gap-6 lg:grid-cols-2">
            <div data-reveal="" className="rounded-[1.75rem] border border-brand-primary/10 p-8 sm:p-10">
              <h3 className="flex items-center gap-3 font-display text-2xl font-semibold text-brand-primary">
                <CircleSlash aria-hidden="true" className="size-6 text-teal-strong" />
                {pricing.notIncluded.title}
              </h3>
              <ul className="mt-6 space-y-3">
                {pricing.notIncluded.items.map((item) => (
                  <li key={item} className="node leading-relaxed text-ink-soft">
                    {item}
                  </li>
                ))}
              </ul>
            </div>
            <div data-reveal="" style={{ ["--reveal-delay" as string]: "90ms" }} className="on-navy rounded-[1.75rem] bg-brand-primary p-8 text-white sm:p-10">
              <h3 className="font-display text-2xl font-semibold">{pricing.conditions.title}</h3>
              <ul className="mt-6 space-y-3">
                {pricing.conditions.items.map((item) => (
                  <li key={item} className="node leading-relaxed text-on-navy-muted">
                    {item}
                  </li>
                ))}
              </ul>
              <TextLink to="/condiciones" tone="light" className="mt-8">
                {pricing.conditions.link}
              </TextLink>
            </div>
          </div>
        </div>
      </section>

      <FaqSection path="/precios" tone="bg" />
      <FinalCta location="pricing_final" />
    </main>
  );
}
